import { BoatCardImage } from '@components/BoatCardImage';
import { DragAndDropIcon } from '@icons';
import { getBoatName } from '@utils/getBoatName';
import { Button, Typography } from 'antd';
import cn from 'classnames';
import { X } from 'lucide-react';
import { FC, useMemo } from 'react';
import { getComparableBoatGallery } from '../hooks/comparableBoatGallery';
import { useComparableBoatsById, useRemoveBoatFromCompare, useSetBoatIndexToShowGallery } from '../hooks/useCompare';
import styles from './ComparableBoatCard.module.scss';
import { ComparableBoatCardProps } from './ComparableBoatCard.props';

const ComparableBoatCard: FC<ComparableBoatCardProps> = ({ boat, itemIndex }) => {
  const boatTitle = getBoatName(boat.attributes);
  const removeBoatFromCompare = useRemoveBoatFromCompare();
  const setBoatIndexToShowGallery = useSetBoatIndexToShowGallery();

  /**
   * The photos live on the Sanity document, not in the store — so the count is zero until the
   * fetch answers, and the "See photos" button stays disabled until there is a gallery to open.
   */
  const comparableBoatsById = useComparableBoatsById();
  const comparableBoat = comparableBoatsById.get(boat._id);

  const nrOfGalleyImages = useMemo(
    () => getComparableBoatGallery(comparableBoat, boat, boatTitle).length,
    [comparableBoat, boat, boatTitle],
  );

  /** The stored thumbnail is what draws the card until the document arrives, then it takes over. */
  const cardImage = comparableBoat?.image ?? boat.attributes.image;

  return (
    <div className={cn('keen-slider__slide', styles.comparableBoatCardWrapper)}>
      <div className={styles.comparableBoatCard}>
        <div className={styles.headerWrapper}>
          <Button type="text">
            <DragAndDropIcon className={styles.actinItemIcon} />
          </Button>
          <Button
            type="text"
            onClick={() => {
              removeBoatFromCompare(boat);
            }}
          >
            <X className={styles.actinItemIcon} />
          </Button>
        </div>
        <div className={styles.cardBodyWrapper}>
          <BoatCardImage height={90} width={160} image={cardImage} />
          <Typography.Title level={3} ellipsis={{ rows: 2 }}>
            {boatTitle}
          </Typography.Title>
        </div>
      </div>
      <div className={styles.footerWrapper}>
        <Button
          onClick={() => setBoatIndexToShowGallery(itemIndex)}
          type="text"
          className={styles.seeMoreBtn}
          disabled={nrOfGalleyImages === 0}
        >
          See photos
          <span>({nrOfGalleyImages})</span>
        </Button>
      </div>
    </div>
  );
};

export { ComparableBoatCard };
